// Claim-support pass (PRD §4b, §13). For a sampled subset of cited references
// where (a) Crossref returned an abstract and (b) the in-text citation marker
// can be located in the manuscript body, ask the LLM whether the abstract
// supports the claim sentence. Emits findings for non-supporting verdicts.

import { callWithSchema } from './output-validator';
import * as claimSupport from './prompts/claim_support';
import type { LlmClient } from './types';
import type { ManuscriptDocument } from '../pdf/types';
import type { ReferenceAuditResult, ReferenceAuditEntry } from '../apis/types';
import { citationByRefNumber, findCitationMarkers } from '../patterns/citations';
import type { PatternFinding } from '../patterns/types';

const SAMPLE_CAP = 5;

export interface ClaimSupportResult {
  findings: PatternFinding[];
  /** How many references were eligible (had abstract + in-text citation). */
  eligibleCount: number;
  /** How many were actually sent to the LLM. */
  sampledCount: number;
  errors: string[];
  modelId: string;
}

function modelIdOf(client: LlmClient): string {
  const state = client.getState();
  return state.kind === 'ready' || state.kind === 'loading' || state.kind === 'idle' || state.kind === 'error'
    ? state.modelId
    : 'unknown';
}

interface EligibleRef {
  entry: ReferenceAuditEntry;
  sentence: string;
  citedTitle: string;
  citedAbstract: string;
}

function gatherEligible(
  doc: ManuscriptDocument,
  audit: ReferenceAuditResult,
): EligibleRef[] {
  const citationMap = citationByRefNumber(findCitationMarkers(doc));
  const out: EligibleRef[] = [];
  for (const entry of audit.entries) {
    if (entry.referenceNumber === null) continue;
    if (entry.outcome.status !== 'resolved' && entry.outcome.status !== 'retracted' && entry.outcome.status !== 'mismatch') {
      continue;
    }
    const work = entry.outcome.work;
    if (!work.abstract || work.abstract.length < 80) continue;
    const cite = citationMap.get(entry.referenceNumber);
    if (!cite) continue;
    out.push({
      entry,
      sentence: cite.sentence,
      citedTitle: work.title,
      citedAbstract: work.abstract.slice(0, 2000),
    });
    if (out.length >= 50) break; // cap eligibility list before sampling
  }
  return out;
}

export interface ClaimSupportArgs {
  client: LlmClient;
  doc: ManuscriptDocument;
  audit: ReferenceAuditResult;
}

export async function runClaimSupportPass(args: ClaimSupportArgs): Promise<ClaimSupportResult | null> {
  if (args.client.getState().kind !== 'ready') return null;
  const eligible = gatherEligible(args.doc, args.audit);
  const modelId = modelIdOf(args.client);
  if (eligible.length === 0) {
    return { findings: [], eligibleCount: 0, sampledCount: 0, errors: [], modelId };
  }

  // Take up to SAMPLE_CAP, prioritising references already flagged (retracted,
  // mismatch) so the LLM corroborates or contradicts those signals.
  const prioritised = [...eligible].sort((a, b) => {
    const score = (e: EligibleRef) => {
      if (e.entry.outcome.status === 'retracted') return 3;
      if (e.entry.outcome.status === 'mismatch') return 2;
      return 1;
    };
    return score(b) - score(a);
  });

  const sampled = prioritised.slice(0, SAMPLE_CAP);
  const errors: string[] = [];
  const findings: PatternFinding[] = [];

  for (const e of sampled) {
    const doi = e.entry.doi ?? `ref${e.entry.referenceNumber}`;
    const req = claimSupport.buildPrompt({
      manuscriptSentence: e.sentence,
      citedTitle: e.citedTitle,
      citedAbstract: e.citedAbstract,
    });
    const result = await callWithSchema(args.client, req, claimSupport.parseResponse);
    if (!result.ok || !result.data) {
      errors.push(`claim_support ref ${e.entry.referenceNumber} failed: ${result.errors?.join('; ') ?? ''}`);
      continue;
    }
    const v = result.data.verdict;
    if (v === 'supports' || v === 'cannot_determine') continue;
    const severity = v === 'does_not_support' ? 'red' : 'amber';
    findings.push({
      id: `claim-support-${doi}-${v}`,
      category: 'references',
      severity,
      checklistItemId: null,
      findingText: `Reference ${e.entry.referenceNumber} (${v.replace('_', ' ')}): ${result.data.rationale}`,
      evidencePassage: e.sentence.slice(0, 240),
      evidenceOffset: -1,
      section: 'unknown',
      layer: 'pattern',
    });
  }

  return {
    findings,
    eligibleCount: eligible.length,
    sampledCount: sampled.length,
    errors,
    modelId,
  };
}
